import React, { useCallback, useId } from 'react';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';

interface ToggleSwitchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'>, ForwardedHTMLProps {
  label?: string;
  description?: string;
  error?: boolean;
  loading?: boolean;
  size?: ComponentSize;
  labelPosition?: 'left' | 'right';
  onLabel?: string;
  offLabel?: string;
  className?: string;
}

const sizeConfig: Record<
  ComponentSize,
  {
    track: string;
    thumb: string;
    translate: string;
    label: string;
    gap: string;
  }
> = {
  xs: { track: 'w-7 h-4', thumb: 'w-3 h-3', translate: 'translate-x-3', label: 'text-xs', gap: 'gap-2' },
  sm: { track: 'w-9 h-5', thumb: 'w-4 h-4', translate: 'translate-x-4', label: 'text-sm', gap: 'gap-2' },
  md: { track: 'w-11 h-6', thumb: 'w-5 h-5', translate: 'translate-x-5', label: 'text-sm', gap: 'gap-3' },
  lg: { track: 'w-14 h-7', thumb: 'w-6 h-6', translate: 'translate-x-7', label: 'text-base', gap: 'gap-3' },
  xl: { track: 'w-16 h-8', thumb: 'w-7 h-7', translate: 'translate-x-8', label: 'text-lg', gap: 'gap-4' },
};

export const ToggleSwitch = React.forwardRef<HTMLInputElement, ToggleSwitchProps>(
  (
    {
      label,
      description,
      error = false,
      loading = false,
      size = 'md',
      labelPosition = 'right',
      onLabel,
      offLabel,
      className,
      checked,
      disabled,
      onChange,
      id,
      ...props
    },
    ref
  ) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);
    const uniqueId = useId();
    const inputId = id || uniqueId;
    const descriptionId = description ? `${inputId}-desc` : undefined;

    const config = sizeConfig[size];
    const isDisabled = disabled || loading;

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLLabelElement>) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          if (!isDisabled && onChange) {
            const syntheticEvent = {
              target: { checked: !checked },
              currentTarget: { checked: !checked },
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(syntheticEvent);
          }
        }
      },
      [isDisabled, onChange, checked]
    );

    const switchTrack = (
      <label
        htmlFor={inputId}
        onKeyDown={handleKeyDown}
        tabIndex={isDisabled ? -1 : 0}
        role="switch"
        aria-checked={checked}
        aria-disabled={isDisabled}
        className={cn(
          'relative inline-flex items-center',
          config.track,
          'rounded-full',
          'cursor-pointer',
          'transition-colors duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          'dark:focus-visible:ring-offset-slate-900',
          checked ? (error ? 'bg-red-500' : 'bg-primary') : 'bg-slate-200 dark:bg-slate-700',
          isDisabled && 'opacity-50 cursor-not-allowed',
          loading && 'animate-pulse'
        )}
      >
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          checked={checked}
          disabled={isDisabled}
          onChange={onChange}
          aria-describedby={descriptionId}
          className="sr-only"
          {...forwardedProps}
          {...props}
        />
        <span
          aria-hidden="true"
          className={cn(
            'absolute top-0.5 left-0.5',
            config.thumb,
            'bg-white',
            'rounded-full',
            'shadow-sm',
            'transition-transform duration-200',
            'flex items-center justify-center',
            checked && config.translate
          )}
        >
          {loading && (
            <span className="w-2/3 h-2/3 border border-slate-300 border-t-transparent rounded-full animate-spin" />
          )}
        </span>
      </label>
    );

    const currentStateLabel = checked ? onLabel : offLabel;

    const textContent = (label || description || currentStateLabel) && (
      <span className="flex flex-col">
        {label && (
          <span className={cn(config.label, 'font-medium', tef.colors.foreground, isDisabled && 'opacity-50')}>
            {label}
          </span>
        )}
        {description && (
          <span id={descriptionId} className={cn('text-xs mt-0.5', tef.colors.foregroundTertiary)}>
            {description}
          </span>
        )}
        {currentStateLabel && !label && (
          <span className={cn(config.label, tef.colors.foregroundSecondary)}>{currentStateLabel}</span>
        )}
      </span>
    );

    return (
      <span
        className={cn('inline-flex items-start', config.gap, labelPosition === 'left' && 'flex-row-reverse', className)}
      >
        {switchTrack}
        {textContent}
      </span>
    );
  }
);

ToggleSwitch.displayName = 'ToggleSwitch';

export default ToggleSwitch;
